class PlannerBoard extends HTMLElement {
  constructor() {
    super();
    this._onDragStart = this._onDragStart.bind(this);
    this._onDragOver  = this._onDragOver.bind(this);
    this._onDrop      = this._onDrop.bind(this);
    this._onTokenSelected = this._onTokenSelected.bind(this);
    this._onTokenRemoved  = this._onTokenRemoved.bind(this);
    // this._onHeadingDblClick = this._onHeadingDblClick.bind(this);
    this._onHeadingInput = this._onHeadingInput.bind(this);
    this._setupHeadingEditing = this._setupHeadingEditing.bind(this);
  }

  connectedCallback() {
    this._container      = this.querySelector('#planner__container');
    this._extraContainer = this.querySelector('.planner__extra-container');
    if (!this._container) return console.error('planner-board needs a #planner__container inside');

    // Cache dataset values once
    const ds = this.dataset;
    this._isWeekly      = ds.weekly === 'true';
    this._isWeeklyDaily = ds.dailyweekly === 'true';
    this._defaultTitle  = ds.defaultTitle || 'My Week';
    this.days           = (ds.days || '').split(',');
    this.circlesNormal  = +ds.circlesNormal || 4;
    this.circlesA3      = +ds.circlesA3 || 5;

    // Event delegation for drag
    this.addEventListener('dragstart', this._onDragStart);
    this.addEventListener('dragover', this._onDragOver);
    this.addEventListener('drop', this._onDrop);

    // Token events
    document.addEventListener('token-selected', this._onTokenSelected);
    document.addEventListener('token-removed', this._onTokenRemoved);

    // Heading edit: delegate dblclick and blur/keydown
    this.querySelectorAll('.planner__heading').forEach(grid => {
    //   grid.addEventListener('dblclick', this._onHeadingDblClick);
      grid.querySelector('.planner__heading-input')
          .addEventListener('blur', this._onHeadingInput);
      grid.querySelector('.planner__heading-input')
          .addEventListener('keydown', e => { if (e.key === 'Enter') e.target.blur(); });
    });

    // Variant-specific columns
    if (this._isWeekly && !this._isWeeklyDaily && this.circlesA3) {
      document.querySelectorAll('.original-selector[name="id"]').forEach(el =>
        el.addEventListener('change', e => this._updatePlannerColumns(e.detail.option2))
      );
    }

    this._setupHeadingEditing();
  }

  _isWeeklyPlanner() {
    return this.dataset && this.dataset.weekly === 'true';
  }

  // Delegated handlers
  _onDragStart(e) {
    const img = e.target.closest('.planner__circle-image[draggable]');
    if (!img) return;
    const circle = img.closest('.planner__circle');
    const id = circle?.dataset.tokenId;
    if (!id) return;
    this._swapSource = circle;
    // only set text/plain for planner-to-planner swaps
    e.dataTransfer.setData('text/plain', id);
  }

  _onDragOver(e) {
    if (e.target.closest('.planner__circle')) e.preventDefault();
  }

  _onDrop(e) {
    e.preventDefault();
    const target = e.target.closest('.planner__circle');
    if (!target) return;
    // JSON payload first (external drags)
    let handled = false;
    try {
      const json = e.dataTransfer.getData('application/json');
      if (json) {
        const data = JSON.parse(json);
        if (data?.id) {
          this._handleDropData(data, target);
          handled = true;
        }
      }
    } catch (__) {}
    if (handled) return;

    // Fallback swap for internal drags
    const id = e.dataTransfer.getData('text/plain');
    if (this._swapSource && id && this._swapSource.dataset.tokenId === id && this._swapSource !== target) {
      this._swapCircles(this._swapSource, target);
      this._swapSource = null;
    }
  }

  _handleDropData({ id, image, name, backgroundColor, textColor}, target) {
    if (this._isWeekly) {
      // If the target is filled, find nearest empty circle
      if (target.dataset.tokenId) {
        const nearest = this._findNearestEmptyCircle(target);
        if (nearest) {
          this._addTokenToCircle(nearest, id, image, name, 1, backgroundColor, textColor);
        }
        // else: no empty spot, do nothing
      } else {
        this._addTokenToCircle(target, id, image, name, 1, backgroundColor, textColor);
      }
    } else {
      this._addOrIncrement(id, image, name, 1, backgroundColor, textColor);
    }
    this.dispatchEvent(new CustomEvent('token-selected', { detail: { id, image, name, count:1, source:'planner' }, bubbles: true, composed: true }));
  }

  _onTokenSelected(e) {
    if (e.detail.source === 'planner') return;
    const { id, image, name, count=1, backgroundColor, textColor } = e.detail;
    this._addOrIncrement(id, image, name, count, backgroundColor, textColor);
  }

  _onTokenRemoved(e) {
    if (e.target === this) return;
    const { id, count=1, internal } = e.detail || {};
    if (internal) return;
    this._decrement(id, count);
  }


  _setupHeadingEditing() {
    this.querySelectorAll('.planner__heading').forEach(heading => {
      const labelSpan  = heading.querySelector('.planner__heading-label');
      const labelInput = heading.querySelector('.planner__heading-input');
      const editBtn    = heading.querySelector('.planner__edit-btn');

      // on pencil-click: swap in the input
      editBtn.addEventListener('click', e => {
        e.stopPropagation();
        labelInput.value = labelSpan.textContent.trim();
        labelSpan.style.display  = 'none';
        labelInput.style.display = '';
        labelInput.focus();
        labelInput.select();
      });

      // on blur or Enter: save back to span
      labelInput.addEventListener('blur', this._onHeadingInput);
      labelInput.addEventListener('keydown', e => {
        if (e.key === 'Enter') e.target.blur();
      });
    });
  }

  _onHeadingInput(e) {
    const input = e.target;
    const span  = input.previousElementSibling;
    span.textContent = input.value.trim() || span.textContent;
    input.style.display = 'none'; span.style.display='';
    this._updateDOWOutput();
  }

  _swapCircles(src, dst) {
    // grab everything we need from each
    const srcImg = src.querySelector('.planner__circle-image');
    const dstImg = dst.querySelector('.planner__circle-image');
    const srcId  = src.dataset.tokenId;
    const dstId  = dst.dataset.tokenId;
    const srcName= src.dataset.tokenName;
    const dstName= dst.dataset.tokenName;
    const srcBG  = srcImg.style.backgroundImage;
    const dstBG  = dstImg.style.backgroundImage;
    const srcLabel = src.querySelector('.planner__text-label');
    const dstLabel = dst.querySelector('.planner__text-label');
    
    // Get background colors from CSS variables
    const srcBgColor = getComputedStyle(src).getPropertyValue('--background-color').trim() || '#fff';
    const dstBgColor = getComputedStyle(dst).getPropertyValue('--background-color').trim() || '#fff';

    // swap background images & data attributes
    srcImg.style.backgroundImage = dstBG;
    dstImg.style.backgroundImage = srcBG;

    src.dataset.tokenId   = dstId   || '';
    src.dataset.tokenName = dstName || '';
    dst.dataset.tokenId   = srcId   || '';
    dst.dataset.tokenName = srcName || '';

    // swap text labels
    srcLabel.textContent = dstName || '';
    dstLabel.textContent = srcName || '';
    
    // swap background colors
    src.style.setProperty('--background-color', dstBgColor);
    dst.style.setProperty('--background-color', srcBgColor);

    // ensure `planner__image__added` is on any circle that has a token
    src.classList.toggle('planner__image__added', !!dstId);
    dst.classList.toggle('planner__image__added', !!srcId);

    this._setRemoveListener(src, src.dataset.tokenId);
    this._setRemoveListener(dst, dst.dataset.tokenId);
  }

    _addOrIncrement(id, image, name, count = 1, backgroundColor = '#fff', textColor = '#000') {
        if (this._isWeeklyPlanner()) {
                const cols       = Array.from(this._container.querySelectorAll('.planner__column'));
                // assume each column has the same number of circles:
                const numPerCol  = cols[0]?.querySelectorAll('.planner__circle').length || 0;
                let emptyCircle  = null;

                // 1) MAIN GRID: row-by-row search
                outer:
                for (let row = 0; row < numPerCol; row++) {
                for (let col of cols) {
                    const circle = col.querySelectorAll('.planner__circle')[row];
                    if (circle && !circle.dataset.tokenId) {
                    emptyCircle = circle;
                    break outer;
                    }
                }
                }

                // 2) FALLBACK: any empty in the extra-container
                if (!emptyCircle) {
                const extraCircles = Array.from(
                    this.querySelectorAll('.planner__extra-container .planner__circle')
                );
                emptyCircle = extraCircles.find(c => !c.dataset.tokenId);
                }

                if (emptyCircle) {
                this._addTokenToCircle(emptyCircle, id, image, name, count, backgroundColor, textColor);
                }
                return;
        }

        let circ = this._container.querySelector(
            `.planner__circle[data-token-id="${id}"]`
        );
        if (circ) {
            this._bump(circ, count);
        } else {
            circ = this._makeCircle(id, image, name, count, backgroundColor, textColor);
            this._container.appendChild(circ);
        }
    }

    _addTokenToCircle(circle, id, image, name, count = 1, backgroundColor = '#fff', textColor = '#000') {
        if (circle.dataset.tokenId) return false; // prevent overwrite, return false

        circle.classList.add('planner__image__added');
        circle.dataset.tokenId = id;
        circle.dataset.tokenName = name;
        
        // Set the background color CSS variable
        circle.style.setProperty('--background-color', backgroundColor);
        circle.style.setProperty('--text-color', textColor);

        const img = circle.querySelector('.planner__circle-image');
        if (img) img.style.backgroundImage = `url(${image})`;

        const span = circle.querySelector('.planner__text-label');
        if (span) span.textContent = name;

        const input = circle.querySelector('.planner__text-input');
        if (input) {
            input.value = name;
            input.addEventListener('blur', () => this._finishEdit(input));
            input.addEventListener('keydown', e => {
            if (e.key === 'Enter') input.blur();
            });
        }

        const editBtn = circle.querySelector('.planner__edit-btn');
        if (editBtn) {
            editBtn.addEventListener('click', ev => {
            ev.stopPropagation();
            this._startEdit(span);
            });
        }

        const remBtn = circle.querySelector('.planner__remove-btn');
        if (remBtn) {
            this._setRemoveListener(circle, id);
        }

        if (!this._isWeeklyPlanner()) {
            const badge = document.createElement('span');
            badge.classList.add('planner__token-count');
            badge.textContent = count;
            circle.appendChild(badge);
        }
        return true;
    }

    _setRemoveListener(circle, tokenId) {
        const remBtn = circle.querySelector('.planner__remove-btn');
        if (!remBtn) return;

        const existing = remBtn.__removeTokenListener;
        if (existing) remBtn.removeEventListener('click', existing);

        const listener = (e) => {
            e.stopPropagation();
            const currentCircle = e.target.closest('.planner__circle');
            if (!currentCircle) return;
            this._decrement(tokenId, 1, false, currentCircle);
        };

        remBtn.addEventListener('click', listener);
        remBtn.__removeTokenListener = listener;
    }

    _decrement(id, count = 1, internal = false, circEl = null) {
        let circ = circEl;
        if (!circEl) {
        circ = this._container.querySelector(`.planner__circle[data-token-id="${id}"]`);
        if (!circ) {
            circ = this.querySelector(`.planner__extra-container .planner__circle[data-token-id="${id}"]`);
        }
        }
        if (!circ) return;

        const badge = circ.querySelector('.planner__token-count');
        const current = parseInt(badge?.textContent || '0', 10);
        const newCount = current - count;

        if (newCount > 0) {
            badge.textContent = newCount;
        } else {
            badge?.remove();
          if (this._isWeeklyPlanner()) {
              // in weekly mode, just clear that slot so the grid stays intact
              circ.dataset.tokenId = '';
              circ.dataset.tokenName = '';
              circ.classList.remove('planner__image__added');
              circ.querySelector('.planner__circle-image').style.backgroundImage = '';
              circ.querySelector('.planner__text-label').textContent = '';
              const inp = circ.querySelector('.planner__text-input');
              if (inp) inp.value = '';
            } else {
              // in non-weekly mode, remove the circle entirely (no more gap)
              circ.remove();
            }
        }

        // dispatch the removal so your TokenGrid can pick it up
        this.dispatchEvent(new CustomEvent('token-removed', {
            detail: { id, count, internal: true },
            bubbles: true, composed: true
        }));
    }

  _bump(circle, count = 1) {
    const badge = circle.querySelector('.planner__token-count');
    badge.textContent = parseInt(badge.textContent, 10) + count;
  }

  _makeCircle(id, image, name, count = 1, backgroundColor = '#fff', textColor = '#000') {
    const c = document.createElement('div');
    c.classList.add('planner__circle', 'planner__image__added');
    c.dataset.tokenId = id;
    c.dataset.tokenName = name;
    c.style.setProperty('--background-color', backgroundColor);
    c.style.setProperty('--text-color', textColor);

    const imgDiv = document.createElement('div');
    imgDiv.classList.add('planner__circle-image');
    imgDiv.style.backgroundImage = `url(${image})`;
    imgDiv.oncontextmenu = () => false;
    imgDiv.dataset.disableContextmenu = '';
    c.appendChild(imgDiv);

    const txt = document.createElement('div');
    txt.classList.add('planner__circle-text');
    const span = document.createElement('span');
    span.classList.add('planner__text-label');
    span.textContent = name;
    const inp = document.createElement('input');
    inp.type = 'text';
    inp.classList.add('planner__text-input');
    inp.style.display = 'none';
    txt.append(span, inp);
    c.appendChild(txt);

    span.addEventListener('dblclick', () => this._startEdit(span));
    inp.addEventListener('blur', () => this._finishEdit(inp));
    inp.addEventListener('keydown', e => { if (e.key === 'Enter') inp.blur(); });

    const badge = document.createElement('span');
    badge.classList.add('planner__token-count');
    badge.textContent = count;
    c.appendChild(badge);

    const rem = document.createElement('button');
    rem.classList.add('planner__remove-btn');
    rem.innerHTML = '&#10006;';
    c.appendChild(rem);

    this._setRemoveListener(c, id); 

    const edt = document.createElement('button');
    edt.classList.add('planner__edit-btn');
    edt.innerHTML = '&#10000;';
    edt.addEventListener('click', ev => {
      ev.stopPropagation();
      this._startEdit(span);
    });
    c.appendChild(edt);

    return c;
  }

  _startEdit(span) {
    const inp = span.nextElementSibling;
    inp.value = span.textContent;
    span.style.display = 'none';
    inp.style.display = '';
    inp.focus();
    inp.select();
  }

  _finishEdit(inp) {
        const span       = inp.previousElementSibling;
        const circle     = inp.closest('.planner__circle');
        const originalId   = circle.dataset.tokenId;
        const originalName = circle.dataset.tokenName || span.textContent;
        const newName      = inp.value.trim() || originalName;

        // always hide the input and show the span
        span.style.display = '';
        inp.style.display  = 'none';

        // if nothing changed, do nothing else
        if (newName === originalName) return;

        // otherwise generate a new ID and update everything
        const newId = `${originalId}-renamed-${Date.now()}`;

        span.textContent         = newName;
        circle.dataset.tokenId   = newId;
        circle.dataset.tokenName = newName;

        // re-wire the remove listener to use the new ID
        const remBtn = circle.querySelector('.planner__remove-btn');
        if (remBtn) this._setRemoveListener(circle, newId);

        // tell the outside world what happened
        this.dispatchEvent(new CustomEvent('planner-token-renamed', {
            detail: { oldId: originalId, newId, newName, from: 'planner' },
            bubbles: true,
            composed: true
        }));
        }


    _updatePlannerColumns(option2) {
    const cols = this._container.querySelectorAll('.planner__column');
    const useA3 = option2 === 'A3';
    const numCircles = useA3 ? this.circlesA3 : this.circlesNormal;

    cols.forEach(col => {
      let circles = Array.from(col.querySelectorAll('.planner__circle'));
      while (circles.length > numCircles) {
        col.removeChild(circles.pop());
        circles = Array.from(col.querySelectorAll('.planner__circle'));
      }
      while (circles.length < numCircles) {
        const circle = document.createElement('div');
        circle.classList.add('planner__circle');
        circle.innerHTML = '<div class="planner__circle-image" draggable="true"></div>' +
                           '<div class="planner__circle-text" oncontextmenu="return false;" data-disable-contextmenu><span class="planner__text-label"></span><input type="text" class="planner__text-input" style="display:none;" /></div>' +
                           '<button class="planner__remove-btn">&#10006;</button>' +
                           '<button class="planner__edit-btn">&#10000;</button>';
        col.appendChild(circle);
        circles.push(circle);
      }
    });
  }

  _startHeadingEdit(span, input) {
    input.value = span.textContent.trim();
    span.style.display  = 'none';
    input.style.display = '';
    input.focus();
    input.select();
  }

  _finishHeadingEdit(input, span) {
    const newText = input.value.trim() || span.textContent;
    span.textContent      = newText;
    input.style.display   = 'none';
    span.style.display    = '';

    this._updateDOWOutput();
  }

  _updateDOWOutput() {
    // gather all seven headings in order
    const days = Array.from(
      this.querySelectorAll('.planner__heading-label')
    ).map(el => el.textContent.trim());
    const dow = document.getElementById('dow__output');
    if (dow) dow.value = days.join(', ');
  }

  // Helper to find the nearest empty circle from a given circle
  _findNearestEmptyCircle(fromCircle) {
    // Get all circles in main grid, row by row
    const cols = Array.from(this._container.querySelectorAll('.planner__column'));
    if (!cols.length) return null;
    const numPerCol = cols[0].querySelectorAll('.planner__circle').length;
    let allCircles = [];
    for (let row = 0; row < numPerCol; row++) {
      for (let col of cols) {
        const circle = col.querySelectorAll('.planner__circle')[row];
        if (circle) allCircles.push(circle);
      }
    }
    // Add extra container circles
    const extraCircles = Array.from(this.querySelectorAll('.planner__extra-container .planner__circle'));
    allCircles = allCircles.concat(extraCircles);
    // Find the first empty circle
    for (let c of allCircles) {
      if (!c.dataset.tokenId) return c;
    }
    return null;
  }
}

customElements.define('planner-board', PlannerBoard);